import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { CrcPipe } from '../../../shared/pipes/crc.pipe';
import {
  ArrowLeftRight,
  Bike,
  LucideIconData,
  Package,
  Store,
  Truck,
} from 'lucide-angular';

import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { BadgeComponent, BadgeVariant } from '../../../shared/ui/badge/badge.component';
import { IconComponent } from '../../../shared/ui/icon/icon.component';
import { CartService } from '../../../shared/services/cart.service';
import {
  Preorder,
  PreorderLine,
  PreorderState,
} from '../../../shared/models/cart.model';
import { toAmount } from '../../../shared/utils/amount';
import { environment } from '../../../../environments/environment';

const FALLBACK_IMAGE_URL = '/images/hero/broken_hero.jpg';
const NOT_FOUND_MASCOT_URL = '/images/logo/empty_cart.png';

/**
 * Los métodos de envío se insertan como líneas del pedido (sin relación
 * `product`, por lo que no tienen imagen). Se identifican por su referencia
 * interna y se muestran con un icono distinto por caso.
 */
type DeliveryLineKind =
  | 'storepickup'
  | 'uber'
  | 'ps_delivery'
  | 'intertienda'
  | 'paqueteria';

const DELIVERY_ICONS: Record<DeliveryLineKind, LucideIconData> = {
  storepickup: Store,
  uber: Bike,
  ps_delivery: Truck,
  intertienda: ArrowLeftRight,
  paqueteria: Package,
};

/**
 * Vista pública de detalle de pedido. El cliente la abre desde un botón en
 * el correo de confirmación (`https://perronstore.com/pedido/{token}`). No
 * requiere autenticación: el token largo (60+ chars) es la credencial.
 *
 * Es solo lectura: muestra líneas, totales con pagos/saldo y datos del
 * cliente. Para el flujo post-checkout celebratorio existe la página
 * separada `/checkout/exito/:token`.
 */
@Component({
  selector: 'app-preorder-detail-page',
  imports: [
    DatePipe,
    CrcPipe,
    RouterLink,
    ButtonComponent,
    BadgeComponent,
    IconComponent,
  ],
  templateUrl: './preorder-detail-page.component.html',
  styleUrl: './preorder-detail-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class PreorderDetailPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly cartService = inject(CartService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly preorder = signal<Preorder | null>(null);
  protected readonly loading = signal(true);
  protected readonly notFound = signal(false);

  /** Saldo pendiente: `total - paid`, no negativo (si pagó de más, queda 0). */
  protected readonly balanceDue = computed(() => {
    const p = this.preorder();
    if (!p) return 0;
    const total = toAmount(p.total);
    const paid = toAmount(p.paid);
    return Math.max(0, total - paid);
  });

  protected readonly isFullyPaid = computed(() => {
    const p = this.preorder();
    if (!p) return false;
    return toAmount(p.paid) >= toAmount(p.total) && toAmount(p.total) > 0;
  });

  /** Re-export local de `toAmount` para el template. */
  protected readonly toAmount = toAmount;

  constructor() {
    const token = this.route.snapshot.paramMap.get('token');
    if (!token) {
      this.notFound.set(true);
      this.loading.set(false);
      return;
    }

    this.cartService
      .getPreorderByToken(token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (p) => {
          // El backend devuelve `null` envuelto cuando no encuentra el token;
          // `unwrapPreorder` deja pasar el null tal cual, así que tratamos
          // ausencia de id como not_found.
          if (!p || typeof p !== 'object' || !('id' in p)) {
            this.notFound.set(true);
          } else {
            this.preorder.set(p);
          }
          this.loading.set(false);
        },
        error: () => {
          this.notFound.set(true);
          this.loading.set(false);
        },
      });
  }

  /** Construye dirección legible a partir de los 5 campos desglosados. */
  protected fullAddress(p: Preorder): string {
    const parts = [p.directions, p.hood, p.district, p.canton, p.province]
      .map((s) => s?.trim())
      .filter((s): s is string => !!s);
    if (parts.length > 0) return parts.join(', ');
    return p.address ?? '';
  }

  /** Resuelve la URL de la primera imagen del producto de una línea. */
  protected lineImageUrl(line: PreorderLine): string {
    const first = line.product?.images?.[0];
    if (!first) return FALLBACK_IMAGE_URL;
    return `${environment.imagesUrl}products/${first.name}`;
  }

  /** Si la línea es un método de delivery, devuelve su icono; si es producto, null. */
  protected deliveryIcon(line: PreorderLine): LucideIconData | null {
    const key = (line.internal_reference ?? line.reference ?? '')
      .toString()
      .trim()
      .toLowerCase();
    if (!key) return null;
    if (key.startsWith('storepickup')) return DELIVERY_ICONS.storepickup;
    if (key.startsWith('uber')) return DELIVERY_ICONS.uber;
    if (key.startsWith('ps_delivery')) return DELIVERY_ICONS.ps_delivery;
    if (key.startsWith('intertienda')) return DELIVERY_ICONS.intertienda;
    if (key.startsWith('paqueter')) return DELIVERY_ICONS.paqueteria;
    return null;
  }

  protected onImageError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (img && !img.src.endsWith(FALLBACK_IMAGE_URL)) {
      img.src = FALLBACK_IMAGE_URL;
    }
  }

  /** Mapping estado → badge variant. */
  protected stateBadgeVariant(state: PreorderState | undefined): BadgeVariant {
    switch (state) {
      case 'entregado':
        return 'new';
      case 'confirmado':
      case 'preparando':
      case 'listo':
      case 'preparado':
      case 'repartiendo':
        return 'highlight';
      case 'nuevo':
      default:
        return 'info';
    }
  }

  /** Etiqueta humana para el estado del pedido. */
  protected stateLabel(state: PreorderState | undefined): string {
    switch (state) {
      case 'nuevo':
        return 'Pendiente de confirmación';
      case 'confirmado':
        return 'Confirmado';
      case 'preparando':
        return 'Preparando';
      case 'listo':
        return 'Listo para entrega';
      case 'preparado':
        return 'Listo (antiguo)';
      case 'repartiendo':
        return 'En tránsito (antiguo)';
      case 'entregado':
        return 'Entregado';
      default:
        return 'Pendiente';
    }
  }

  protected readonly notFoundMascotUrl = NOT_FOUND_MASCOT_URL;
}
